import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CardBox } from '~/components/ui/Card';
import { type IconName } from '~/components/ui/Icon';
import SettingsRow from '~/components/ui/SettingsRow';
import useCurrentUser from '~/modules/user/useCurrentUser';

type PersonalDataItemProps = {
  icon: IconName;
  label: string;
  value?: string | null;
  url: string;
  isLast?: boolean;
};

function PersonalDataItem({ icon, label, value, url, isLast }: PersonalDataItemProps) {
  const queryClient = useQueryClient();

  const handleClick = () => {
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const fullUrl = `${window.__CONFIG.VITE_AUTH_URL}${url}`;

    const popup = window.open(
      fullUrl,
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no`,
    );

    if (!popup) return;

    const checkWindowClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkWindowClosed);
        queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      }
    }, 500);
  };

  return (
    <SettingsRow
      icon={icon}
      label={label}
      value={value || '—'}
      layout="split"
      onClick={handleClick}
      isLast={isLast}
    />
  );
}

export default function PersonalData() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const username = user?.username || `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold text-section-title">{t('personalData.title')}</h2>
      <CardBox className="p-0">
        <PersonalDataItem
          icon="alternate_email"
          label={t('personalData.username')}
          value={username}
          url="/account/change-username"
        />
        <PersonalDataItem
          icon="phone_iphone"
          label={t('personalData.phone')}
          value={user?.phone}
          url="/account/change-phone"
        />
        <PersonalDataItem
          icon="mail"
          label={t('personalData.email')}
          value={user?.email}
          url="/account/change-email"
          isLast
        />
      </CardBox>
    </section>
  );
}
